import { useState, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Plus, Search, Package, Edit2, Trash2, ShoppingCart, AlertTriangle } from 'lucide-react';
import { useProductStore, useCategoryStore, useCartStore } from '../../hooks';
import { Button, Card, CardContent, useToast, Badge, Select } from '@lancar-pos/ui';
import { formatCurrency } from '@lancar-pos/shared';

export function ProductsPage() {
  const navigate = useNavigate();
  const { products, deleteProduct } = useProductStore();
  const { categories } = useCategoryStore();
  const { addItem } = useCartStore();
  const toast = useToast();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [productToDelete, setProductToDelete] = useState<typeof products[0] | null>(null);

  const categoryOptions = useMemo(() => {
    return categories
      .filter((c) => !c._deleted)
      .map((c) => ({ value: c.id, label: c.name }));
  }, [categories]);

  const filteredProducts = useMemo(() => {
    return products
      .filter((p) => !p._deleted)
      .filter((p) => {
        if (selectedCategory === 'all') return true;
        return p.categoryId === selectedCategory;
      })
      .filter((p) => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
          p.name.toLowerCase().includes(query) ||
          p.sku.toLowerCase().includes(query) ||
          p.barcode.toLowerCase().includes(query)
        );
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [products, selectedCategory, searchQuery]);

  const handleDelete = (product: typeof products[0]) => {
    setProductToDelete(product);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;
    
    setDeletingId(productToDelete.id);
    try {
      await deleteProduct(productToDelete.id);
      toast.success('Produk dihapus');
    } catch (error) {
      toast.error('Gagal menghapus produk');
    } finally {
      setDeletingId(null);
      setShowDeleteDialog(false);
      setProductToDelete(null);
    }
  };

  const handleQuickAdd = (product: typeof products[0]) => {
    if (product.variants && product.variants.length > 0) {
      toast.info('Pilih varian di POS terminal');
      navigate({ to: '/app' });
    } else {
      if (product.stock <= 0) {
        toast.error('Produk habis', 'Stok tidak tersedia');
        return;
      }
      addItem(product);
    }
  };

  const getCategoryName = (categoryId: string | null) => {
    if (!categoryId) return '-';
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || '-';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => window.history.back()}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">Inventori Produk</h1>
          </div>
          <Button onClick={() => navigate({ to: '/app/inventory/products/$productId', params: { productId: 'new' } })}>
            <Plus className="w-4 h-4 mr-2" />
            Tambah Produk
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 lg:p-6 space-y-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cari produk..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <Select
                options={[{ value: 'all', label: 'Semua Kategori' }, ...categoryOptions]}
                value={selectedCategory}
                onChange={(value) => setSelectedCategory(value)}
                placeholder="Pilih Kategori"
                className="w-full sm:w-40 md:w-44 lg:w-48"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Produk
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      SKU
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Kategori
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Harga
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Stok
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Aksi
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredProducts.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                        <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>Belum ada produk</p>
                        <p className="text-sm text-gray-400">
                          {searchQuery ? 'Coba kata kunci lain' : 'Tambah produk pertama Anda'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    filteredProducts.map((product) => (
                      <tr key={product.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{product.imageUrl || '📦'}</span>
                            <div>
                              <p className="font-medium text-gray-900">{product.name}</p>
                              {product.variants && product.variants.length > 0 && (
                                <Badge variant="default" size="sm">
                                  {product.variants.length} varian
                                </Badge>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                          {product.sku || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {getCategoryName(product.categoryId)}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                          {formatCurrency(product.price)}
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-600">
                          {product.variants && product.variants.length > 0
                            ? `${product.variants.reduce((sum, v) => sum + (v.stock || 0), 0)} total`
                            : product.stock}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant={product.isActive ? 'success' : 'default'}>
                            {product.isActive ? 'Aktif' : 'Nonaktif'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleQuickAdd(product)}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Tambah ke keranjang"
                            >
                              <ShoppingCart className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() =>
                                navigate({ to: '/app/inventory/products/$productId', params: { productId: product.id } })
                              }
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(product)}
                              disabled={deletingId === product.id}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                              title="Hapus"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>

      {showDeleteDialog && productToDelete && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowDeleteDialog(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Hapus Produk?</h2>
            </div>
            <p className="text-gray-600 mb-6">
              Produk "{productToDelete.name}" akan dihapus. Tindakan ini tidak dapat dibatalkan.
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deletingId === productToDelete.id}>
                Batal
              </Button>
              <Button variant="danger" onClick={confirmDelete} loading={deletingId === productToDelete.id}>
                Ya, Hapus
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
